import { useState, useCallback, useRef } from 'react';
import { Typography, Table, Input, Tag, Button, Avatar } from 'antd';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getStudents } from '@/services/students';
import { useMediaQuery } from '@/hooks/useMediaQuery';

dayjs.extend(relativeTime);

interface Student { id: string; name: string; email: string; phone?: string | null; status: string; gradeIds: number[]; lastActiveAt: string | null; _count?: { assignments?: number; studentConnections?: number }; }

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  ACTIVE:    { color: '#065f46', bg: '#d1fae5' },
  SUSPENDED: { color: '#991b1b', bg: '#fee2e2' },
  INACTIVE:  { color: '#6b7280', bg: '#f3f4f6' },
};

const PAGE_SIZE = 20;

export default function StudentsPage() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const page = cursorHistory.length + 1;
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({
    queryKey: ['students', search, cursor],
    queryFn: () => getStudents({ search: search || undefined, cursor, limit: PAGE_SIZE }),
  });

  const result = data as { data?: { data?: Student[]; meta?: { cursor?: string; hasMore?: boolean } } } | undefined;
  const students = result?.data?.data ?? [];
  const meta = result?.data?.meta;
  const total = (meta as Record<string, unknown>)?.total as number ?? 0;

  function handleNext() { setCursorHistory((h) => [...h, cursor]); setCursor(meta?.cursor); }
  function handlePrev() { const h = [...cursorHistory]; const prev = h.pop(); setCursorHistory(h); setCursor(prev); }
  const handleSearch = useCallback((value: string) => { setSearch(value); setCursor(undefined); setCursorHistory([]); }, []);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(value); setCursor(undefined); setCursorHistory([]); }, 300);
  }, []);

  const columns = [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (name: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{name}</span>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string) => <span style={{ color: '#64748b' }}>{v}</span> },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v: string | undefined) => <span style={{ color: '#64748b' }}>{v || '—'}</span> },
    {
      title: 'Grade', key: 'grade',
      render: (_: unknown, r: Student) => r.gradeIds?.map((g) => <Tag key={g} style={{ borderRadius: 12 }}>Grade {g}</Tag>) ?? '—',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: string) => {
        const s = STATUS_STYLES[v] ?? STATUS_STYLES.INACTIVE;
        return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>{v}</span>;
      },
    },
    { title: 'Teachers', key: 'teachers', render: (_: unknown, r: Student) => <Tag style={{ borderRadius: 12 }}>{r._count?.studentConnections ?? 0}</Tag> },
    { title: 'Assignments', key: 'assignments', render: (_: unknown, r: Student) => <Tag style={{ borderRadius: 12 }}>{r._count?.assignments ?? 0}</Tag> },
    {
      title: 'Last Active', dataIndex: 'lastActiveAt', key: 'lastActiveAt',
      render: (v: string | null) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v ? dayjs(v).fromNow() : 'Never'}</span>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Students</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Manage student accounts and their progress</Typography.Text>
      </div>

      <Input
        placeholder="Search students…"
        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
        allowClear
        onChange={(e) => {
          if (!e.target.value) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            handleSearch('');
          } else {
            handleSearchChange(e);
          }
        }}
        style={{
          flex: "1 1 180px",
          maxWidth: 260,
          height: 32,
          marginBottom: 16,
        }}
      />

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : students.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No students found</div>
          ) : students.map((r) => {
            const s = STATUS_STYLES[r.status] ?? STATUS_STYLES.INACTIVE;
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/admin/users/${r.id}`)}
                style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Avatar size={38} style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', fontWeight: 700, flexShrink: 0, fontSize: 15 }}>
                    {r.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{r.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                    {r.phone && <div style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.phone}</div>}
                  </div>
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, flexShrink: 0 }}>
                    {r.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                  {(r.gradeIds ?? []).map((g) => (
                    <Tag key={g} style={{ borderRadius: 12, margin: 0, fontSize: 11 }}>Grade {g}</Tag>
                  ))}
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    <span style={{ color: '#94a3b8' }}>Teachers </span>
                    <Tag style={{ borderRadius: 12, margin: 0 }}>{r._count?.studentConnections ?? 0}</Tag>
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    <span style={{ color: '#94a3b8' }}>Decks </span>
                    <Tag style={{ borderRadius: 12, margin: 0 }}>{r._count?.assignments ?? 0}</Tag>
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                    {r.lastActiveAt ? dayjs(r.lastActiveAt).fromNow() : 'Never'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={students}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
          onRow={(record) => ({ onClick: () => navigate(`/admin/users/${record.id}`), style: { cursor: 'pointer' } })}
        />
      )}

      <div className="cw-pagination">
        <span className="cw-page-info">Page {page}{total > 0 && <> &middot; {total.toLocaleString()} student{total !== 1 ? 's' : ''}</>}</span>
        <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={cursorHistory.length === 0}>Previous</Button>
        <Button icon={<RightOutlined />} onClick={handleNext} disabled={!meta?.hasMore}>Next</Button>
      </div>
    </div>
  );
}
